"use client";
import { motion } from "framer-motion";
import { useState, useRef, useEffect, useMemo } from "react";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  InfoWindow,
} from "@react-google-maps/api";
import { resolveVenueLabel } from "@/components/EventSummary";
import { IconMapPin, IconLocation, IconPlus, IconMinus } from "@/lib/icons";

const mapContainerStyle = {
  width: "100%",
  height: "220px",
  borderRadius: "12px",
};

const mapOptions = {
  minZoom: 5,
  maxZoom: 18,
  disableDefaultUI: true,
  zoomControl: false,
  mapTypeControl: false,
  streetViewControl: false,
  fullscreenControl: false,
  gestureHandling: "cooperative",
  scrollwheel: false,
  styles: [
    {
      featureType: "poi",
      elementType: "labels",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "transit",
      elementType: "labels",
      stylers: [{ visibility: "off" }],
    },
  ],
};

export default function VenueLocationCard({
  mapQuery,
  venue,
  venueObject,
  onNavigate,
  yetToBeAnnounced,
}) {
  const [activeMarker, setActiveMarker] = useState(false);
  const [logoImg, setLogoImg] = useState(null);
  const mapRef = useRef(null);
  const hoverTimeoutRef = useRef(null);

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_KEY || "",
  });

  useEffect(() => {
    const img = new Image();
    img.onload = () => setLogoImg(img);
    img.src = "/logo.png";
  }, []);

  const handleMouseOver = () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    setActiveMarker(true);
  };

  const handleMouseOut = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setActiveMarker(false);
    }, 250);
  };

  const getCenter = () => {
    const tryPair = (lat, lng) => {
      const la = Number(lat);
      const lo = Number(lng);
      if (
        Number.isFinite(la) &&
        Number.isFinite(lo) &&
        (la !== 0 || lo !== 0)
      ) {
        return { lat: la, lng: lo };
      }
      return null;
    };

    const sources = [
      venueObject,
      venueObject?.venue,
      venueObject?.location,
      venueObject?.event?.venue,
      typeof venue === "object" ? venue : null,
      typeof venue === "object" ? venue?.venue : null,
    ].filter(Boolean);

    for (const src of sources) {
      if (src?.location?.lat && src?.location?.lng) {
        const c = tryPair(src.location.lat, src.location.lng);
        if (c) return c;
      }
      if (src?.lat && (src?.long || src?.lng)) {
        const c = tryPair(src.lat, src.long || src.lng);
        if (c) return c;
      }
      if (src?.latitude && (src?.longitude || src?.lng)) {
        const c = tryPair(src.latitude, src.longitude || src.lng);
        if (c) return c;
      }
      if (src?.geometry?.location?.lat && src?.geometry?.location?.lng) {
        const c = tryPair(
          typeof src.geometry.location.lat === "function"
            ? src.geometry.location.lat()
            : src.geometry.location.lat,
          typeof src.geometry.location.lng === "function"
            ? src.geometry.location.lng()
            : src.geometry.location.lng,
        );
        if (c) return c;
      }
    }

    if (mapQuery?.includes(",")) {
      const [a, b] = mapQuery.split(",");
      const c = tryPair(a, b);
      if (c) return c;
    }
    return null;
  };

  const center = getCenter();
  const addrObj =
    typeof venueObject?.address === "object" && venueObject?.address !== null
      ? venueObject.address
      : typeof venue?.address === "object" && venue?.address !== null
        ? venue.address
        : null;

  const venueCity =
    venueObject?.city ||
    addrObj?.city ||
    (typeof venue === "object" ? venue?.city : null) ||
    null;

  const venueState =
    venueObject?.state ||
    addrObj?.state ||
    (typeof venue === "object" ? venue?.state : null) ||
    null;

  const venuePincode =
    venueObject?.pincode ||
    addrObj?.pincode ||
    (typeof venue === "object" ? venue?.pincode : null) ||
    null;

  const venueName =
    venueObject?.name ||
    addrObj?.name ||
    resolveVenueLabel(venue) ||
    null;

  const line1 =
    (typeof venueObject?.line1 === "string" ? venueObject.line1 : addrObj?.line1 || "")?.trim() || "";
  const line2 =
    (typeof venueObject?.line2 === "string" ? venueObject.line2 : addrObj?.line2 || "")?.trim() || "";
  const rawStreetAddress = [line1, line2].filter(Boolean).join(", ");

  const cityStatePincode = [
    venueCity,
    venueState,
    venuePincode,
  ]
    .filter(Boolean)
    .join(", ");

  const hasCityInStreet =
    rawStreetAddress &&
    venueCity &&
    typeof venueCity === "string" &&
    rawStreetAddress.toLowerCase().includes(venueCity.toLowerCase());

  const stringAddress =
    typeof venueObject?.address === "string"
      ? venueObject.address
      : typeof venueObject?.formatted_address === "string"
        ? venueObject.formatted_address
        : typeof venue === "string"
          ? venue
          : "";

  const displayAddress = rawStreetAddress || stringAddress || "";

  const displayLocationSubtext =
    displayAddress && !hasCityInStreet && cityStatePincode
      ? `${displayAddress}, ${cityStatePincode}`
      : displayAddress || cityStatePincode || "";

  const markerIcon = useMemo(() => {
    if (!isLoaded || !logoImg || typeof window === "undefined")
      return undefined;

    // Match QR `.qrLogo`: circular crop + thick white ring + soft shadow.
    // Draw at devicePixelRatio so the white border stays crisp on the map.
    const displaySize = 56;
    const ring = 8; // ~QR padding(3) + box-shadow ring(3), scaled up for map
    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(displaySize * dpr);
    canvas.height = Math.round(displaySize * dpr);
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);

    const cx = displaySize / 2;
    const cy = displaySize / 2;
    const outerR = displaySize / 2;
    const innerR = outerR - ring;

    // Soft drop shadow (QR: 0 2px 8px rgba(2,21,38,0.3))
    ctx.save();
    ctx.shadowColor = "rgba(2, 21, 38, 0.3)";
    ctx.shadowBlur = 6;
    ctx.shadowOffsetY = 2;
    ctx.beginPath();
    ctx.arc(cx, cy, outerR - 0.5, 0, Math.PI * 2);
    ctx.fillStyle = "#ffffff";
    ctx.fill();
    ctx.restore();

    // Solid white disc (the round border)
    ctx.beginPath();
    ctx.arc(cx, cy, outerR, 0, Math.PI * 2);
    ctx.fillStyle = "#ffffff";
    ctx.fill();

    // Circular logo inside — clip so square /logo.png becomes a round badge
    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cy, innerR, 0, Math.PI * 2);
    ctx.closePath();
    ctx.clip();
    ctx.drawImage(
      logoImg,
      ring,
      ring,
      displaySize - ring * 2,
      displaySize - ring * 2,
    );
    ctx.restore();

    // Extra crisp white stroke on the outer edge (like QR's 0 0 0 3px #fff)
    ctx.beginPath();
    ctx.arc(cx, cy, outerR - 1.25, 0, Math.PI * 2);
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = 2.5;
    ctx.stroke();

    return {
      url: canvas.toDataURL("image/png"),
      scaledSize: new window.google.maps.Size(displaySize, displaySize),
      anchor: new window.google.maps.Point(displaySize / 2, displaySize / 2),
      origin: new window.google.maps.Point(0, 0),
    };
  }, [isLoaded, logoImg]);

  const handleOpenDirections = () => {
    const placeId =
      venueObject?.google_place_id ||
      venueObject?.place_id ||
      venueObject?.placeId;
    if (placeId) {
      const query = encodeURIComponent(displayAddress || venueName || "Venue");
      window.open(
        `https://www.google.com/maps/search/?api=1&query=${query}&query_place_id=${placeId}`,
        "_blank",
      );
    } else if (center?.lat && center?.lng) {
      window.open(
        `https://www.google.com/maps/dir/?api=1&destination=${center.lat},${center.lng}`,
        "_blank",
      );
    } else if (onNavigate) {
      onNavigate();
    } else if (displayAddress) {
      window.open(
        `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(displayAddress)}`,
        "_blank",
      );
    }
  };
  useEffect(() => {
    const timer = setTimeout(() => {
      document.querySelectorAll(".gm-style-iw-ch").forEach((el) => {
        el.style.display = "none";
      });

      document.querySelectorAll(".gm-style-iw-c").forEach((el) => {
        el.style.paddingTop = "0";
      });
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  const handleDomReady = () => {
    setTimeout(() => {
      document.querySelectorAll(".gm-style-iw-chr").forEach((el) => {
        el.style.display = "none";
      });

      document.querySelectorAll(".gm-ui-hover-effect").forEach((el) => {
        el.style.display = "none";
      });

      document.querySelectorAll(".gm-style-iw-c").forEach((el) => {
        el.style.padding = "0";
      });
    }, 0);
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-30px" }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className="bg-white border border-[#e3e6e9] rounded-2xl overflow-hidden"
    >
      {/* Map */}
      {yetToBeAnnounced ? (
        /* Venue not confirmed yet — don't show a map/pin for a placeholder address */
        <div className="flex items-center gap-3 px-5 py-4">
          <div className="w-8 h-8 rounded-full bg-[#f3f6f9] flex items-center justify-center shrink-0">
            <IconMapPin size={15} className="text-[rgba(2,21,38,0.5)]" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-[#021526] m-0 truncate">
              Venue to be announced
            </p>
            {venueCity && (
              <p className="text-xs text-[rgba(2,21,38,0.55)] m-0">
                {venueCity}
              </p>
            )}
          </div>
        </div>
      ) : center ? (
        <>
          {isLoaded ? (
            <div className="p-4 pb-0">
            <div className="relative overflow-hidden rounded-xl border border-[#eef1f4]">
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              center={center}
              zoom={15}
              options={mapOptions}
              onLoad={(map) => {
                mapRef.current = map;
              }}
            >
              <Marker
                position={center}
                icon={markerIcon}
                optimized={false}
                onMouseOver={handleMouseOver}
                onMouseOut={handleMouseOut}
                onClick={handleOpenDirections}
              >
                {activeMarker && (
                  <InfoWindow
                    onCloseClick={() => setActiveMarker(false)}
                    options={{
                      disableAutoPan: false,
                      pixelOffset:
                        isLoaded && window.google?.maps
                          ? new window.google.maps.Size(0, -6)
                          : undefined,
                    }}
                  >
                    <div
                      onMouseEnter={handleMouseOver}
                      onMouseLeave={handleMouseOut}
                      style={{
                        padding: "4px 6px !imoprtant",
                        maxWidth: "240px",
                        fontFamily: "inherit",
                      }}
                    >
                      {venueName && (
                        <div
                          style={{
                            margin: "0 0 4px",
                            fontWeight: 700,
                            fontSize: "13px",
                            color: "#021526",
                            lineHeight: 1.3,
                          }}
                        >
                          {venueName}
                        </div>
                      )}
                      {displayLocationSubtext && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: "6px",
                            fontSize: "12px",
                            color: "#4a5568",
                            marginBottom: "8px",
                            lineHeight: 1.35,
                          }}
                        >
                          <IconMapPin
                            size={14}
                            style={{
                              color: "#021526",
                              flexShrink: 0,
                              marginTop: "2px",
                            }}
                          />
                          <span
                            style={{
                              display: "-webkit-box",
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {displayLocationSubtext}
                          </span>
                        </div>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenDirections();
                        }}
                        style={{
                          width: "100%",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "6px",
                          padding: "6px 12px",
                          backgroundColor: "#021526",
                          color: "#ffffff",
                          border: "none",
                          borderRadius: "8px",
                          cursor: "pointer",
                          fontSize: "12px",
                          fontWeight: 600,
                          boxShadow: "0 2px 8px rgba(2, 21, 38, 0.15)",
                          transition: "background-color 0.2s ease",
                        }}
                      >
                        <IconLocation size={14} />
                        Get Directions
                      </button>
                    </div>
                  </InfoWindow>
                )}
              </Marker>
            </GoogleMap>

              <div className="absolute right-2.5 top-2.5 z-[1] flex flex-col overflow-hidden rounded-md border border-[#e3e6e9] bg-white shadow-sm">
                <button
                  type="button"
                  aria-label="Zoom in"
                  onClick={() => {
                    const map = mapRef.current;
                    if (!map) return;
                    map.setZoom(Math.min((map.getZoom() || 15) + 1, 18));
                  }}
                  className="w-7 h-7 inline-flex items-center justify-center text-[#021526] hover:bg-[#f3f6f9] transition border-b border-[#eef1f4]"
                >
                  <IconPlus size={14} stroke={2.2} />
                </button>
                <button
                  type="button"
                  aria-label="Zoom out"
                  onClick={() => {
                    const map = mapRef.current;
                    if (!map) return;
                    map.setZoom(Math.max((map.getZoom() || 15) - 1, 5));
                  }}
                  className="w-7 h-7 inline-flex items-center justify-center text-[#021526] hover:bg-[#f3f6f9] transition"
                >
                  <IconMinus size={14} stroke={2.2} />
                </button>
              </div>
            </div>
            </div>
          ) : (
            <div className="px-4 pt-4">
            <div
              style={{ height: 220 }}
              className="rounded-xl border border-[#eef1f4] bg-[#f3f6f9] flex items-center justify-center text-xs text-[rgba(2,21,38,0.4)]"
            >
              Loading map…
            </div>
            </div>
          )}

          {/* Venue detail strip below the map */}
          <div className="flex items-center justify-between gap-4 px-5 py-4">
            <div className="flex items-start gap-3 min-w-0 flex-1">
              <span className="w-9 h-9 rounded-full bg-[#f3f6f9] border border-[#e3e6e9] flex items-center justify-center shrink-0">
              <IconMapPin
                size={16}
                className="text-[#021526]"
              />
              </span>
              <div className="min-w-0 py-0.5">
                {venueName && (
                  <p className="text-sm font-semibold text-[#021526] m-0 leading-snug">
                    {venueName}
                  </p>
                )}
                {displayLocationSubtext && (
                  <p className="text-xs text-[rgba(2,21,38,0.65)] m-0 mt-1 leading-relaxed line-clamp-2">
                    {displayLocationSubtext}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={handleOpenDirections}
              className="shrink-0 flex items-center gap-1.5 text-xs font-semibold text-[#021526] border border-[#e3e6e9] rounded-lg px-3.5 py-2 bg-[#f3f6f9] hover:bg-[#e9edf1] transition cursor-pointer"
            >
              <IconLocation size={14} />
              Directions
            </button>
          </div>
        </>
      ) : (
        /* No coords — plain venue pill */
        <div className="flex items-center gap-3 px-5 py-4">
          <div className="w-8 h-8 rounded-full bg-[#f3f6f9] flex items-center justify-center shrink-0">
            <IconMapPin size={15} className="text-[rgba(2,21,38,0.5)]" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-[#021526] m-0 truncate">
              {venueName || "Location details available soon."}
            </p>
            {venueCity && (
              <p className="text-xs text-[rgba(2,21,38,0.55)] m-0">
                {venueCity}
              </p>
            )}
          </div>
        </div>
      )}
    </motion.div>
  );
}
